import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";

import ColorOptionControls from "features/option-controls/color-option-controls";
import GlassOptionControls from "features/option-controls/glass-option-controls";
import HandleOptionControls from "features/option-controls/handle-option-controls";

import { useScreenSize } from "app/hooks/shared/useScreenSize";
import GewoonzekerBlock from "entities/shared/gewoonzeker-block";
import { DoorWidthControls } from "features/option-controls/door-width-controls/DoorWidthControls";
import {
  AllXLXXLDoorCodes,
  enabledXLXXLDeMaasDeWaalNewDoors,
} from "features/option-controls/door-width-controls/XLXXLHelper";
import { motion } from "framer-motion";
import OptionTemplate from "shared/ui/option-template";
import { OptionTemplateAnimated } from "shared/ui/option-template-animated";
import { OptionModalState } from "widgets/configurator/option-modal";
import { DoorOptionsControls } from "../door-options-controls";
import { EigenwijsOptionSetup } from "../eigenwijs-option-setup";
import { ExtraOptionsBlock } from "./extra-option-controls/ExtraOptionsBlock";
import { SelfAssemblyBlock } from "./SelfAssemblyBlock";
import { LayoutNavigationTransition } from "widgets/configurator/layout-navigation-transition";

const DOORS_WITH_DISABLED_HANDLE_SELECTION = [
  "paneel",
  "paneelpaneel",
  "paneelpaneelpaneel",
  "paneelpaneelpaneelpaneel",
];

interface Props {
  setOptionModalState: (state: OptionModalState) => void;
}
const DoorOptions = ({ setOptionModalState }: Props) => {
  const {
    handle,
    color,
    layout,
    glass,
    showBeamHeightValidationError,
    selectedDoor,
    selfAssembleDoorData,
    isNotMaasWaalIjsselLayout,
  } = useConfigurationOptionsContext();

  const { isMobileScreen } = useScreenSize();
  const isEigenwijs = layout?.code === "eigenwijs";
  const layoutViewKey = isEigenwijs ? "eigenwijs" : "options";

  return (
    <>
      {!isMobileScreen && (
        <SelfAssemblyBlock setOptionModalState={setOptionModalState} />
      )}

      <OptionTemplateAnimated testId="door-layout-option">
        <LayoutNavigationTransition viewKey={layoutViewKey}>
          {isEigenwijs ? (
            <EigenwijsOptionSetup />
          ) : (
            <DoorOptionsControls
              setOptionModalState={setOptionModalState}
              showMeasurementInputs={!selfAssembleDoorData.selfAssembly}
            />
          )}
        </LayoutNavigationTransition>
      </OptionTemplateAnimated>

      <OptionTemplate
        title="Kies jouw kleur"
        description="Een passende kleur voor je maatwerk deur die aansluit bij iedere woonstijl. Kies je voor tijdloos zwart, iets zachter grijs óf toch voor warm brons?"
        selected={color}
        testId="door-color-option"
        onShowInfoClick={() =>
          setOptionModalState({
            isOpen: true,
            heading: "Kies jouw kleur",
            description:
              "We hebben diverse kleuren geselecteerd, zodat er altijd wel een framekleur is die bij jouw interieur past! Ga jij voor klassiek zwart of wil je juist meer kleur?",
          })
        }
      >
        <ColorOptionControls />
      </OptionTemplate>

      <OptionTemplate
        title="Kies soort glas"
        description="Meer licht in de woonkamer of juist 100% privacy op het toilet? Ontdek onze glassoorten en creëer het mooiste effect."
        testId="door-glass-option"
        onShowInfoClick={() =>
          setOptionModalState({
            isOpen: true,
            heading: "Kies soort glas",
            description:
              "Meer licht in de woonkamer of juist 100% privacy op het toilet? Ontdek onze glassoorten en " +
              "creëer het mooiste effect.",
          })
        }
        selected={glass}
      >
        <GlassOptionControls />
      </OptionTemplate>

      {selectedDoor &&
        DOORS_WITH_DISABLED_HANDLE_SELECTION.includes(selectedDoor.code) ===
          false && (
          <OptionTemplate
            title="Kies deurbeslag"
            selected={handle}
            testId="door-handle-option"
            onShowInfoClick={() =>
              setOptionModalState({
                isOpen: true,
                heading: "Kies deurbeslag",
                description:
                  "Bij iedere deur is standaard deurbeslag inbegrepen. Wil je liever iets anders? " +
                  "Maak dan hieronder je keuze.",
              })
            }
          >
            <HandleOptionControls />
            {showBeamHeightValidationError && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="gg-mt-4 gg-text-sm gg-text-red"
              >
                Let op: De U-greep kan niet worden toegepast in de huidige
                configuratie. U-grepen kunnen tot een maximale lengte van 2100mm
                worden gemaakt. Voeg een extra ligger toe of maak de afstand
                tussen de huidige liggers kleiner.
              </motion.p>
            )}
          </OptionTemplate>
        )}

      {selectedDoor?.code &&
        AllXLXXLDoorCodes.includes(selectedDoor.code) &&
        (isNotMaasWaalIjsselLayout ||
          enabledXLXXLDeMaasDeWaalNewDoors.includes(selectedDoor.code)) && (
          <OptionTemplate
            title="Breedte deur"
            description="Wil je een normale afmeting deur, of wil je een hele grote deur? Aan jou de keuze."
            testId="door-color-width"
            onShowInfoClick={() =>
              setOptionModalState({
                isOpen: true,
                heading: "Breedte deur",
                description:
                  "Alle deuren worden per bestelling op maat gemaakt. Is jouw deur breder dan 115 centimeter, dan heet dat bij ons een XL deur. Is jouw deur breder dan 145 centimeter? Dan is jouw deur een XXL variant.",
              })
            }
          >
            <DoorWidthControls />
          </OptionTemplate>
        )}

      {isMobileScreen && (
        <SelfAssemblyBlock setOptionModalState={setOptionModalState} />
      )}

      <ExtraOptionsBlock setOptionModalState={setOptionModalState} />

      <GewoonzekerBlock setOptionModalState={setOptionModalState} />
    </>
  );
};
export default DoorOptions;
